/* eslint-disable branded-effects/raw-interop-boundary, no-raw-nulls/no-raw-nulls, no-void-undefined-signatures/no-void-undefined-signatures */
import * as E from "fp-ts/Either";
import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useListingInferenceReviewPortalClient } from "@contexts/ListingInferenceReviewPortalClient.context";
import { ok } from "@ps-bridge/ok";
import type { ListingInferenceReviewPortalProgramInput } from "@ps-bridge/listingInferenceReviewPortal.program";
import { buildListingInferenceReviewPortalViewProps as buildListingInferenceReviewPortalViewPropsPs } from "@ps-bridge/listingInferenceReviewPortal.program";
import type {
  ListingInferenceClaimId,
  ListingInferenceClaimKind,
  ListingInferenceDecisionReason,
  ListingInferenceDisposition,
  ListingInferenceListingId,
  ListingInferenceReviewer,
  ListingInferenceSourceRef,
} from "@ps-bridge/models/brands/listingInferenceReviewPortal";
import type { OptionTs } from "@ps-bridge/interop";
import { optionNone, optionSome } from "@src/purescript/interop";
import type {
  ListingInferenceEvidenceRow,
  ListingInferenceEvidenceQueryResult,
  ListingInferenceManualDecisionResult,
  ListingInferenceProvisionalClaim,
  ListingInferenceReviewError,
  ListingInferenceWorkspaceReadResult,
} from "@services/clients/ListingInferenceReviewPortalApi";
import { toRawTaskEither } from "@utils/effects/branded";
import { enabledFeatures, FeaturesEnum } from "@utils/enabledFeatures";
import { useListingInferenceReviewPortalLoad } from "@view/listing/inference/reviewPortal/useListingInferenceReviewPortalLoad";
import type {
  ListingInferenceReviewPortalClaimRow,
  ListingInferenceReviewPortalDecisionDraft,
  ListingInferenceReviewPortalGapRow,
  ListingInferenceReviewPortalPhase,
  ListingInferenceReviewPortalQueueItem,
  ListingInferenceReviewPortalSummary,
} from "@view/listing/inference/reviewPortal/ListingInferenceReviewPortalPhase";
import { selectListingInferenceReviewPortalRouteShellPhase } from "@view/listing/inference/reviewPortal/ListingInferenceReviewPortalPhase";
import ListingInferenceReviewPortalPanel from "@view/panels/listing/inference/ListingInferenceReviewPortal.panel";
import * as Option from "@utils/Option";

type RouteListingId = OptionTs<ListingInferenceListingId>;
type DraftClaimId = OptionTs<ListingInferenceClaimId>;
type DraftTargetDisposition = OptionTs<ListingInferenceDisposition>;
type DraftReviewer = OptionTs<ListingInferenceReviewer>;
type DraftReason = OptionTs<ListingInferenceDecisionReason>;
type RuntimeBrandString =
  | string
  | { readonly value: string }
  | { readonly value0: string }
  | { readonly toString: () => string }
  | undefined;
type PortalData = {
  readonly workspace: ListingInferenceWorkspaceReadResult;
  readonly evidence: ListingInferenceEvidenceQueryResult;
};

const emptySummary: ListingInferenceReviewPortalSummary = {
  proposed: 0,
  confirmed: 0,
  rejected: 0,
  conflicted: 0,
};

const emptyDraft: ListingInferenceReviewPortalDecisionDraft = {
  claimId: "",
  targetDisposition: "confirmed",
  reviewer: "broker-reviewer",
  reason: "",
};

const reviewQueue: readonly ListingInferenceReviewPortalQueueItem[] = [
  {
    listingId: "018f3ad5-39d4-7bd8-9f79-5bb5f6f34c92",
    label: "Acme workspace",
    nextReview: "Party identity needs manual confirmation",
  },
  {
    listingId: "listing-456",
    label: "Direct listing follow-up",
    nextReview: "Open provenance gap review",
  },
];

const entryPhaseFor = (
  portalEnabled: boolean,
  routeListingIdValue: string,
  reviewQueueItems: readonly ListingInferenceReviewPortalQueueItem[],
): ListingInferenceReviewPortalPhase =>
  selectListingInferenceReviewPortalRouteShellPhase({
    portalEnabled,
    hasRouteListingId: routeListingIdValue.length > 0,
    hasReviewQueueItems: reviewQueueItems.length > 0,
    canReadWorkspace: true,
  });

const buildListingInferenceReviewPortalViewProps = (
  input: ListingInferenceReviewPortalProgramInput,
): ReturnType<typeof buildListingInferenceReviewPortalViewPropsPs> =>
  buildListingInferenceReviewPortalViewPropsPs(input);

const waitForSubmittedRefreshingPaint = async (): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, 0);
  });

const withEffectiveDispositionSummary = (
  workspace: ListingInferenceWorkspaceReadResult,
  decisionResult: ListingInferenceManualDecisionResult,
): ListingInferenceWorkspaceReadResult => ({
  ...workspace,
  workspace: {
    ...workspace.workspace,
    effectiveDispositionSummary: decisionResult.effectiveDispositionSummary,
  },
  effectiveDispositionSummary: decisionResult.effectiveDispositionSummary,
});

const isValidRouteListingId = (listingId: string | undefined): listingId is string =>
  typeof listingId === "string" && listingId.trim().length > 0 && !listingId.includes(" ");

const toRouteListingId = (listingId: string | undefined): RouteListingId =>
  isValidRouteListingId(listingId)
    ? (optionSome(listingId.trim()) as RouteListingId)
    : (optionNone() as RouteListingId);

const runtimeBrandStringValue = (value: RuntimeBrandString): string => {
  if (typeof value === "string") {
    return value;
  }
  if (!value) {
    return "";
  }
  if ("value0" in value) {
    return value.value0;
  }
  if ("value" in value) {
    return value.value;
  }
  const rendered = value.toString();
  return rendered === "[object Object]" ? "" : rendered;
};

const evidenceClaimId = (row: ListingInferenceEvidenceRow): string =>
  runtimeBrandStringValue(row.claimId as RuntimeBrandString);

const workspaceClaimId = (row: ListingInferenceProvisionalClaim | undefined): string =>
  runtimeBrandStringValue(row?.id as RuntimeBrandString);

const resolvedClaimId = (
  workspaceClaim: ListingInferenceProvisionalClaim | undefined,
  evidenceRow: ListingInferenceEvidenceRow,
): string => workspaceClaimId(workspaceClaim) || evidenceClaimId(evidenceRow);

const toClaimRows = (data: OptionTs<PortalData>): readonly ListingInferenceReviewPortalClaimRow[] =>
  Option.fold(data, () => [], (portalData) =>
    portalData.evidence.evidenceRows.map((row, index) => {
      const workspaceClaim = portalData.workspace.claims[index];
      return {
        id: resolvedClaimId(workspaceClaim, row),
        kind: String(workspaceClaim?.kind ?? row.kind),
        value: row.value,
        disposition: String(workspaceClaim?.disposition ?? row.disposition),
        sourceRef: String(workspaceClaim?.provenance.sourceRef ?? row.provenance.sourceRef),
        speaker: row.provenance.speaker,
        observedAt: row.provenance.observedAt,
        nonFactStatus: row.nonFactStatus,
      };
    }),
  );

const toGapRows = (data: OptionTs<PortalData>): readonly ListingInferenceReviewPortalGapRow[] =>
  Option.fold(data, () => [], (portalData) => portalData.evidence.gapRows.map((row) => ({
    question: row.gap.question,
    sourceRef: String(row.gap.evidenceSource),
    status: row.rejectedOrConflicted ? "rejected-or-conflicted-evidence-gap" : row.nonFactStatus,
  })));

const errorPhase = (error: ListingInferenceReviewError): ListingInferenceReviewPortalPhase => {
  switch (error.error) {
    case "readDenied":
      return "readDenied";
    case "writeDenied":
      return "writeDeniedReadOnly";
    case "unsafeFactPromotionRefused":
      return "unsafeFactPromotionRefused";
    default:
      return "loadFailed";
  }
};

const ListingInferenceReviewPortalPage: React.FC = () => {
  const params = useParams();
  const routeListingId = params.listingId;
  const routeListingIdValue = isValidRouteListingId(routeListingId) ? routeListingId.trim() : "";
  const client = useListingInferenceReviewPortalClient();
  const hasProgramBoundary = typeof buildListingInferenceReviewPortalViewPropsPs === "function";
  const portalEnabled = enabledFeatures[FeaturesEnum.ListingInferenceReviewPortal];
  const [phase, setPhase] = useState<ListingInferenceReviewPortalPhase>(
    entryPhaseFor(portalEnabled, routeListingIdValue, reviewQueue),
  );
  const [selectedListingId, setSelectedListingId] = useState(routeListingIdValue);
  const [directListingId, setDirectListingId] = useState(routeListingIdValue);
  const [data, setData] = useState<OptionTs<PortalData>>(Option.none());
  const [selectedClaimId, setSelectedClaimId] = useState("");
  const [filterDisposition, setFilterDisposition] = useState("all");
  const [filterKind, setFilterKind] = useState("all");
  const [includeGaps, setIncludeGaps] = useState(true);
  const [draft, setDraft] = useState<ListingInferenceReviewPortalDecisionDraft>(emptyDraft);
  const [errorMessage, setErrorMessage] = useState("");
  const [acceptedDecision, setAcceptedDecision] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const listingId = routeListingIdValue.length > 0 ? routeListingIdValue : selectedListingId;

  const filteredClaims = useMemo(() => {
    const rows = toClaimRows(data);
    return rows.filter(
      (row) =>
        (filterDisposition === "all" || row.disposition === filterDisposition) &&
        (filterKind === "all" || row.kind === filterKind),
    );
  }, [data, filterDisposition, filterKind]);

  const selectedClaim =
    filteredClaims.length === 0
      ? Option.none<ListingInferenceReviewPortalClaimRow>()
      : Option.some(filteredClaims.find((claim) => claim.id === selectedClaimId) ?? filteredClaims[0]);
  const selectedClaimTargetId = Option.fold(selectedClaim, () => "", (claim) => claim.id);
  const selectedClaimLabel = Option.fold(selectedClaim, () => "", (claim) => claim.value);
  const summary = Option.fold(data, () => emptySummary, (portalData) => portalData.workspace.effectiveDispositionSummary);
  const derivedDraft: ListingInferenceReviewPortalDecisionDraft = {
    ...draft,
    claimId: selectedClaimTargetId,
  };

  const loadWorkspace = useCallback(async () => {
    if (!portalEnabled) {
      setPhase("featureUnavailable");
      return ok;
    }
    if (routeListingId !== undefined && !isValidRouteListingId(routeListingId)) {
      setErrorMessage("Invalid listing id in route.");
      setPhase("loadFailed");
      return ok;
    }
    if (listingId.length === 0) {
      setData(Option.none());
      setSelectedClaimId("");
      setDraft((current) => ({ ...current, claimId: "" }));
      setPhase(entryPhaseFor(portalEnabled, routeListingIdValue, reviewQueue));
      return ok;
    }

    setPhase("loading");
    setErrorMessage("");
    const workspaceResult = await toRawTaskEither(client.readWorkspace(listingId as ListingInferenceListingId))();
    if (E.isLeft(workspaceResult)) {
      setErrorMessage(workspaceResult.left.message);
      setPhase(errorPhase(workspaceResult.left));
      return ok;
    }

    const evidenceResult = await toRawTaskEither(
      client.queryEvidence({
        listingId: listingId as ListingInferenceListingId,
        claimKinds: filterKind === "all" ? [] : [filterKind as ListingInferenceClaimKind],
        dispositions: filterDisposition === "all" ? [] : [filterDisposition as ListingInferenceDisposition],
        sourceRefs: [] as readonly ListingInferenceSourceRef[],
        unresolvedOnly: false,
        includeGaps,
        unsafeFactPromotion: false,
      }),
    )();
    if (E.isLeft(evidenceResult)) {
      setErrorMessage(evidenceResult.left.message);
      setPhase(errorPhase(evidenceResult.left));
      return ok;
    }

    setData(Option.some({
      workspace: workspaceResult.right,
      evidence: evidenceResult.right,
    }));
    const firstClaimId =
      evidenceResult.right.evidenceRows.length > 0
        ? resolvedClaimId(workspaceResult.right.claims[0], evidenceResult.right.evidenceRows[0])
        : "";
    setSelectedClaimId(firstClaimId);
    setDraft((current) => ({
      ...current,
      claimId: firstClaimId.length > 0 ? firstClaimId : current.claimId,
    }));
    setPhase("loadedWorkspace");
    return ok;
  }, [client, filterDisposition, filterKind, includeGaps, listingId, portalEnabled, routeListingId, routeListingIdValue]);

  useListingInferenceReviewPortalLoad(loadWorkspace, reloadToken);

  const programInput: ListingInferenceReviewPortalProgramInput = {
    kernel: {},
    routeListingId: toRouteListingId(routeListingId),
    portalEnabled,
    canReadWorkspace: true,
    canWriteDecision: phase !== "writeDeniedReadOnly",
    workspaceLoaded: Option.fold(data, () => false, () => true),
    workspaceLoadFailed: phase === "loadFailed",
    decisionSubmitting: phase === "submittingDecision",
    decisionSubmitFailed: phase === "submitFailed",
    decisionAcceptedRefreshing: phase === "submittedRefreshing",
    summaryUpdated: phase === "summaryUpdated",
    unsafeFactPromotionAttempted: phase === "unsafeFactPromotionRefused",
    draftClaimId:
      selectedClaimTargetId.length > 0 ? (optionSome(selectedClaimTargetId) as DraftClaimId) : (optionNone() as DraftClaimId),
    draftTargetDisposition:
      draft.targetDisposition.length > 0
        ? (optionSome(draft.targetDisposition) as DraftTargetDisposition)
        : (optionNone() as DraftTargetDisposition),
    draftReviewer:
      draft.reviewer.length > 0 ? (optionSome(draft.reviewer) as DraftReviewer) : (optionNone() as DraftReviewer),
    draftReason: draft.reason.length > 0 ? (optionSome(draft.reason) as DraftReason) : (optionNone() as DraftReason),
  };
  const viewProps = buildListingInferenceReviewPortalViewProps(programInput);
  const loadedPhase = Option.fold(
    data,
    () => "loadedWorkspace" as ListingInferenceReviewPortalPhase,
    () => (selectedClaimTargetId.length === 0 ? "noSelectedClaim" : "loadedWorkspace"),
  );
  const panelPhaseByPhase: Record<ListingInferenceReviewPortalPhase, ListingInferenceReviewPortalPhase> = {
    featureUnavailable: "featureUnavailable",
    queueEmpty: "queueEmpty",
    noListingSelected: "noListingSelected",
    loading: viewProps.phase as ListingInferenceReviewPortalPhase,
    loadedWorkspace: loadedPhase,
    noSelectedClaim: "noSelectedClaim",
    readDenied: "readDenied",
    loadFailed: "loadFailed",
    writeDeniedReadOnly: "writeDeniedReadOnly",
    submittingDecision: "submittingDecision",
    submitFailed: "submitFailed",
    submittedRefreshing: "submittedRefreshing",
    summaryUpdated: "summaryUpdated",
    unsafeFactPromotionRefused: "unsafeFactPromotionRefused",
  };
  const panelPhase = panelPhaseByPhase[phase];

  const submitDecision = useCallback(async () => {
    if (listingId.length === 0 || selectedClaimTargetId.length === 0) {
      setErrorMessage("Decision requires a loaded listing and selected claim.");
      setPhase("submitFailed");
      return;
    }

    setPhase("submittingDecision");
    setErrorMessage("");
    const result = await toRawTaskEither(
      client.submitDecision({
        listingId: listingId as ListingInferenceListingId,
        claimId: selectedClaimTargetId as ListingInferenceClaimId,
        targetDisposition: draft.targetDisposition as ListingInferenceDisposition,
        reviewer: draft.reviewer as ListingInferenceReviewer,
        reason: draft.reason as ListingInferenceDecisionReason,
        unsafeFactPromotion: false,
      }),
    )();
    if (E.isLeft(result)) {
      setErrorMessage(result.left.message);
      setPhase(errorPhase(result.left) === "writeDeniedReadOnly" ? "writeDeniedReadOnly" : "submitFailed");
      return;
    }

    const decisionResult: ListingInferenceManualDecisionResult = result.right;
    setAcceptedDecision(`${selectedClaimLabel} ${draft.targetDisposition}; ready for next review`);
    setPhase("submittedRefreshing");
    await waitForSubmittedRefreshingPaint();
    if (decisionResult.refresh.kind === "included") {
      const refreshedWorkspace = withEffectiveDispositionSummary(decisionResult.refresh.workspace, decisionResult);
      setData((current) =>
        Option.fold(current, () => current, (portalData) =>
          Option.some({
            ...portalData,
            workspace: refreshedWorkspace,
          }),
        ),
      );
    } else {
      setData((current) =>
        Option.fold(current, () => current, (portalData) =>
          Option.some({
            ...portalData,
            workspace: withEffectiveDispositionSummary(portalData.workspace, decisionResult),
          }),
        ),
      );
    }
    setPhase("summaryUpdated");
  }, [client, draft, listingId, selectedClaimLabel, selectedClaimTargetId]);

  const requestUnsafeFactPromotion = useCallback(async () => {
    if (listingId.length === 0 || selectedClaimTargetId.length === 0) {
      setPhase("unsafeFactPromotionRefused");
      setErrorMessage("Unsafe fact promotion refused before any CIM fact mutation.");
      return;
    }

    const result = await toRawTaskEither(
      client.submitDecision({
        listingId: listingId as ListingInferenceListingId,
        claimId: selectedClaimTargetId as ListingInferenceClaimId,
        targetDisposition: draft.targetDisposition as ListingInferenceDisposition,
        reviewer: draft.reviewer as ListingInferenceReviewer,
        reason: draft.reason as ListingInferenceDecisionReason,
        unsafeFactPromotion: true,
      }),
    )();
    setPhase("unsafeFactPromotionRefused");
    setErrorMessage(E.isLeft(result) ? result.left.message : "Unsafe fact promotion refused before any CIM fact mutation.");
  }, [client, draft, listingId, selectedClaimTargetId]);

  const selectListing = useCallback((nextListingId: string) => {
    const trimmedListingId = nextListingId.trim();
    setSelectedListingId(trimmedListingId);
    setDirectListingId(trimmedListingId);
    setSelectedClaimId("");
    setDraft((current) => ({ ...current, claimId: "" }));
    setAcceptedDecision("");
    setReloadToken((value) => value + 1);
  }, []);

  const selectClaim = useCallback((claimId: string) => {
    setSelectedClaimId(claimId);
    setDraft((current) => ({ ...current, claimId }));
  }, []);

  const selectNextReview = useCallback(() => {
    if (filteredClaims.length === 0) {
      return;
    }
    const currentIndex = filteredClaims.findIndex((claim) => claim.id === selectedClaimTargetId);
    const nextClaim = filteredClaims[(currentIndex + 1) % filteredClaims.length] ?? filteredClaims[0];
    setSelectedClaimId(nextClaim.id);
    setDraft((current) => ({ ...current, claimId: nextClaim.id }));
  }, [filteredClaims, selectedClaimTargetId]);

  return (
    <ListingInferenceReviewPortalPanel
      acceptedDecision={acceptedDecision}
      claims={filteredClaims}
      draft={derivedDraft}
      errorMessage={errorMessage}
      filterDisposition={filterDisposition}
      filterKind={filterKind}
      gaps={toGapRows(data)}
      hasProgramBoundary={hasProgramBoundary}
      hasProvider={Boolean(client)}
      includeGaps={includeGaps}
      listingId={listingId.length > 0 ? listingId : viewProps.listingLabel}
      directListingId={directListingId}
      onDraftChange={setDraft}
      onDirectListingIdChange={setDirectListingId}
      onFilterDispositionChange={setFilterDisposition}
      onFilterKindChange={setFilterKind}
      onIncludeGapsChange={setIncludeGaps}
      onLoadDirectListing={() => selectListing(directListingId)}
      onNextReview={selectNextReview}
      onRequestUnsafeFactPromotion={requestUnsafeFactPromotion}
      onRetryLoad={() => setReloadToken((value) => value + 1)}
      onRetrySubmit={submitDecision}
      onSelectClaim={selectClaim}
      onSelectListing={selectListing}
      onSubmitDecision={submitDecision}
      phase={panelPhase}
      readOnly={phase === "writeDeniedReadOnly"}
      reviewQueue={reviewQueue}
      selectedClaim={selectedClaim}
      selectedClaimLabel={selectedClaimLabel}
      summary={summary}
    />
  );
};

export default ListingInferenceReviewPortalPage;
